import axios, {
  AxiosError,
  type AxiosRequestTransformer,
  type AxiosResponse,
} from 'axios'
import { snakeCase } from 'snake-case'
import { camelCase } from 'camel-case'
import qs from 'qs'
import type { InviteBoard } from './message'
import type { CargoValue, Commodity, OrderInfoWithDetail } from './order'
import type {
  Estimation,
  EstimatorResponse,
  QuickEstimatorResponse,
} from './estimator'

interface ConvertFunc {
  (name: string): string
}

function _convertObject(
  obj: {
    [key: string]: any
    [key: number]: any
  },
  fn: ConvertFunc,
) {
  let r: any = {}
  Object.keys(obj).forEach((k) => {
    r[fn(k)] = changeProps(obj[k], fn)
  })
  return r
}

function _convertArray(arr: Array<any>, fn: ConvertFunc): Array<any> {
  return arr.map((x) => changeProps(x, fn))
}

export function changeProps(obj: any, fn: ConvertFunc) {
  if (!obj) {
    return obj
  } else if (obj instanceof Array) {
    return _convertArray(obj, fn)
  } else if (typeof obj === 'object') {
    return _convertObject(obj, fn)
  }
  return obj
}

export function camelProps(obj: any) {
  return changeProps(obj, camelCase)
}
export function snakeProps(obj: any) {
  return changeProps(obj, snakeCase)
}

const defaultRequestTransformers = (): AxiosRequestTransformer[] => {
  const { transformRequest } = axios.defaults
  if (!transformRequest) {
    return []
  } else if (transformRequest instanceof Array) {
    return transformRequest
  } else {
    return [transformRequest]
  }
}

const createAPI = () => {
  const config = useRuntimeConfig()
  const account = useAccountStore()
  const router = useRouter()

  const { apiEndpoint } = config.public

  const { $i18n } = useNuxtApp()

  const http = axios.create({
    baseURL: apiEndpoint,
    transformRequest: [
      (data, headers) => {
        return snakeProps(data)
      },
      ...defaultRequestTransformers(),
    ],

    paramsSerializer: (params: any) =>
      qs.stringify(snakeProps(params), { indices: false }),
  })

  http.interceptors.response.use(
    (res) => {
      if (res.headers['content-type'] === 'application/json') {
        return camelProps(res.data)
      } else {
        return res
      }
    },
    async (error) => {
      const response = error.response
      if (response) {
        error.status = error.statusCode = error.response.status
      }
      if (error.status === 401) {
        // 401の場合は自動でログアウトを実施
        //　おかしなことにならないか検証が必要
        await account.logout()
        let next = '/admin/login'
        if (config.public.auth0ClientId) {
          next = '/login'
        }
        const currentPath = router.currentRoute.value.fullPath
        //await router.push({ path: next, query: { next: currentPath } })
        await navigateTo({ path: next, query: { next: currentPath } })
        // 一旦これで問題が出ないか様子見
        return Promise.resolve(undefined)
      }
      return Promise.reject(error)
    },
  )
  http.interceptors.request.use(async (config) => {
    // const { locale } = useI18n();
    const idToken = await account.getIdToken()
    if (idToken) {
      const value = `Bearer ${idToken}`
      config.headers.set('Authorization', value)
    }
    const accessToken = await account.getAccessToken()
    if (accessToken) {
      const value = `JWT ${accessToken}`
      config.headers.set('Authorization', value)
    }

    config.headers.set('Accept-Language', $i18n.locale.value)
    return config
  })

  return {
    http,

    auth: {
      obtainToken: (data: any): Promise<{ access: string; refresh: string }> =>
        http.post('v1/auth/', data),
      // NOTE: refresh tokenはrequest interceptorを使用させないため、axiosを直接使用す必要がある
      // request interceptorでaccessTokenの期限切れを検知しているため無限再起が発生する
      refreshToken: (refresh: string): Promise<{ access: string }> =>
        axios
          .post('v1/auth/refresh/', { refresh }, { baseURL: apiEndpoint })
          .then((res) => camelProps(res.data)),
    },

    countries: (id?: string) => ({
      list: (params?: any): Promise<Paginated<Country>> =>
        http.get('v1/countries/', { params }),
      get: (): Promise<Country> => http.get(`v1/countries/${id}/`),
    }),
    account: {
      get: (): Promise<MyUser> => http.get('v1/my/'),
    },

    users: (id?: string) => ({
      get: (params?: any): Promise<User> =>
        http.get(`v1/users/${id}/`, { params }),
      list: (params?: any): Promise<Paginated<User>> =>
        http.get('v1/users/', { params }),
      create: (data: any): Promise<User> => http.post('v1/users/', data),
      update: (data: any): Promise<User> => http.put(`v1/users/${id}/`, data),
      patch: (data: any): Promise<User> => http.patch(`v1/users/${id}/`, data),
      delete: (): Promise<void> => http.delete(`v1/users/${id}/`),
      sendPasswordResetMail: () =>
        http.post(`v1/users/${id}/send_password_reset/`),
    }),

    staff: (id?: string) => ({
      get: (params?: any): Promise<User> =>
        http.get(`v1/staff/${id}/`, { params }),
      list: (params?: any): Promise<Paginated<User>> =>
        http.get('v1/staff/', { params }),

      create: (data: any): Promise<User> => http.post('v1/staff/', data),
      update: (data: any): Promise<User> => http.patch(`v1/staff/${id}/`, data),
      delete: (): Promise<void> => http.delete(`v1/staff/${id}/`),
      sendInviteMail: () => http.post(`v1/staff/${id}/send_invitation/`),
    }),
    clients: (id?: string | number) => ({
      get: (params?: any): Promise<Organization> =>
        http.get(`v1/clients/${id}/`, { params }),
      list: (params?: any): Promise<Paginated<Organization>> =>
        http.get('v1/clients/', { params }),
      create: (data: any): Promise<Organization> =>
        http.post('v1/clients/', data),
      update: (data: any): Promise<Organization> =>
        http.put(`v1/clients/${id}/`, data),

      poas: (poaId?: string) => ({
        get: (params?: any): Promise<CustomsPoa> =>
          http.get(`v1/clients/${id}/poas/${poaId}/`, { params }),
        list: (params?: any): Promise<Paginated<CustomsPoa>> =>
          http.get(`v1/clients/${id}/poas/`, { params }),
        create: (data: any): Promise<CustomsPoa> =>
          http.post(`v1/clients/${id}/poas/`, data),
        update: (data: any): Promise<CustomsPoa> =>
          http.put(`v1/clients/${id}/poas/${poaId}/`, data),
      }),
    }),
    partners: (id?: string | number) => ({
      get: (params?: any): Promise<Organization> =>
        http.get(`v1/partners/${id}/`, { params }),
      list: (params?: any): Promise<Paginated<Organization>> =>
        http.get('v1/partners/', { params }),
      create: (data: any): Promise<Organization> =>
        http.post('v1/partners/', data),
      update: (data: any): Promise<Organization> =>
        http.put(`v1/partners/${id}/`, data),
    }),
    agencies: (id?: string | number) => ({
      get: (params?: any): Promise<Organization> =>
        http.get(`v1/agencies/${id}/`, { params }),
      list: (params?: any): Promise<Paginated<Organization>> =>
        http.get('v1/agencies/', { params }),
      create: (data: any): Promise<Organization> =>
        http.post('v1/agencies/', data),
      update: (data: any): Promise<Organization> =>
        http.put(`v1/agencies/${id}/`, data),
    }),
    organizations: (id?: string | number) => ({
      get: (params?: any): Promise<Organization> =>
        http.get(`v1/organizations/${id}/`, { params }),
      list: (params?: any): Promise<Paginated<Organization>> =>
        http.get('v1/organizations/', { params }),

      create: (data: any): Promise<Organization> =>
        http.post('v1/organizations/', data),
      update: (data: any): Promise<Organization> =>
        http.put(`v1/organizations/${id}/`, data),

      members: (memberId?: string) => ({
        get: (params?: any): Promise<User> =>
          http.get(`v1/organizations/${id}/members/${memberId}/`, { params }),
        list: (params?: any): Promise<Paginated<User>> =>
          http.get(`v1/organizations/${id}/members/`, { params }),
        create: (data: any): Promise<User> =>
          http.post(`v1/organizations/${id}/members/`, data),
        update: (data: any): Promise<User> =>
          http.patch(`v1/organizations/${id}/members/${memberId}/`, data),
      }),

      locations: (locationId?: string) => ({
        get: (params?: any): Promise<BaseLocation> =>
          http.get(`v1/organizations/${id}/locations/${locationId}/`, {
            params,
          }),
        list: (params?: any): Promise<Paginated<BaseLocation>> =>
          http.get(`v1/organizations/${id}/locations/`, { params }),
        create: (data: any): Promise<BaseLocation> =>
          http.post(`v1/organizations/${id}/locations/`, data),
        update: (data: any): Promise<BaseLocation> =>
          http.patch(`v1/organizations/${id}/locations/${locationId}/`, data),
      }),

      tradingPartners: (tradingPartnerId?: string) => ({
        get: (params?: any): Promise<TradingPartner> =>
          http.get(
            `v1/organizations/${id}/trading_partners/${tradingPartnerId}/`,
            {
              params,
            },
          ),
        list: (params?: any): Promise<Paginated<TradingPartner>> =>
          http.get(`v1/organizations/${id}/tradingpartners/`, { params }),
        create: (data: any): Promise<TradingPartner> =>
          http.post(`v1/organizations/${id}/tradingpartners/`, data),
        update: (data: any): Promise<TradingPartner> =>
          http.patch(
            `v1/organizations/${id}/tradingpartners/${tradingPartnerId}/`,
            data,
          ),
      }),
      items: (itemId?: string) => ({
        get: (params?: any): Promise<Item> =>
          http.get(`v1/organizations/${id}/items/${itemId}/`, { params }),
        list: (params?: any): Promise<Paginated<Item>> =>
          http.get(`v1/organizations/${id}/items/`, { params }),
        create: (data: any): Promise<Item> =>
          http.post(`v1/organizations/${id}/items/`, data),
        update: (data: any): Promise<Item> =>
          http.patch(`v1/organizations/${id}/items/${itemId}/`, data),
      }),
    }),

    orderRequests: (id?: string) => ({
      get: (params?: any): Promise<OrderRequest> =>
        http.get(`v1/orders/requests/${id}/`, { params }),
      list: (params?: any): Promise<Paginated<OrderRequest>> =>
        http.get('v1/orders/requests/', { params }),
      order: (data: any): Promise<Order> =>
        http.post(`v1/orders/requests/${id}/order/`, data),
      update_details: (data: any): Promise<OrderRequest> =>
        http.patch(`v1/orders/requests/${id}/update_details/`, data),
    }),

    orders: (id?: string) => ({
      get: (params?: any): Promise<Order> =>
        http.get(`v1/orders/${id}/`, { params }),
      list: (params?: any): Promise<Paginated<Order>> =>
        http.get('v1/orders/', { params }),
      create: (data: any): Promise<Order> => http.post(`v1/orders/`, data),
      update: (data: any): Promise<Order> => http.put(`v1/orders/${id}/`, data),
      patch: (data: any): Promise<Order> =>
        http.patch(`v1/orders/${id}/`, data),
      toEstimated: (data: any): Promise<Order> =>
        http.post(`v1/orders/${id}/to_estimated/`, data),
      toPreordered: (data: any): Promise<Order> =>
        http.post(`v1/orders/${id}/to_preordered/`, data),
      toCancelled: (data: any): Promise<Order> =>
        http.post(`v1/orders/${id}/to_cancelled/`, data),
      createD2D: (data?: CreateD2DTask): Promise<D2DTask> =>
        http.post(`v1/orders/${id}/create_d2d/`, data),
      createTasks: (data?: CreateD2DTask): Promise<D2DTask> =>
        http.post(`v1/orders/${id}/create_tasks/`, data),

      changeService: (data: ChangeServiceValue): Promise<Order> =>
        http.post(`v1/orders/${id}/change_service/`, data),

      // 調達費用(受注と1:1のシングルトン)
      procurementExpense: () => ({
        get: (): Promise<ProcurementExpense> =>
          http.get(`v1/orders/${id}/procurement_expense/`),
        update: (data: any): Promise<ProcurementExpense> =>
          http.put(`v1/orders/${id}/procurement_expense/`, data),
        confirm: (): Promise<ProcurementExpense> =>
          http.post(`v1/orders/${id}/procurement_expense/confirm/`),
      }),

      estimates: (estimateId?: string) => ({
        list: (params?: any): Promise<Paginated<InvoiceWithDetail>> =>
          http.get(`v1/orders/${id}/estimates/`, { params }),
        get: (): Promise<Paginated<InvoiceWithDetail>> =>
          http.get(`v1/orders/${id}/estimates/${estimateId}/`),
        create: (data: any): Promise<InvoiceWithDetail> =>
          http.post(`v1/orders/${id}/estimates/`, data),
        update: (data: any): Promise<InvoiceWithDetail> =>
          http.put(`v1/orders/${id}/estimates/${estimateId}/`, data),
        patch: (data: any): Promise<InvoiceWithDetail> =>
          http.patch(`v1/orders/${id}/estimates/${estimateId}/`, data),
        revert: (params?: any): Promise<Order> =>
          http.post(`v1/orders/${id}/estimates/${estimateId}/revert/`, {
            params,
          }),
        confirm: (params?: any): Promise<Order> =>
          http.post(`v1/orders/${id}/estimates/${estimateId}/confirm/`, {
            params,
          }),
        approve: (): Promise<Order> =>
          http
            .post(`v1/orders/${id}/estimates/${estimateId}/approve/`)
            .then((res: any) => {
              useApprovalRequestStore().refresh()
              return res
            }),
        messages: (mid?: number) => ({
          create: (data: any): Promise<Message> =>
            http.post(`v1/orders/${id}/messages/`, data),
          list: (params?: any): Promise<Paginated<Message>> =>
            http.get(`v1/orders/${id}/messages/`, { params }),
        }),

        board: () => ({
          get: (): Promise<Board> => http.get(`v1/orders/${id}/board/`),
        }),
      }),
      invoices: (estimateId?: string) => ({
        list: (params?: any): Promise<Paginated<InvoiceWithDetail>> =>
          http.get(`v1/orders/${id}/invoices/`, { params }),
        get: (): Promise<Paginated<InvoiceWithDetail>> =>
          http.get(`v1/orders/${id}/invoices/${estimateId}/`),
        create: (data: any): Promise<InvoiceWithDetail> =>
          http.post(`v1/orders/${id}/invoices/`, data),
        update: (data: any): Promise<InvoiceWithDetail> =>
          http.put(`v1/orders/${id}/invoices/${estimateId}/`, data),
        patch: (data: any): Promise<InvoiceWithDetail> =>
          http.patch(`v1/orders/${id}/invoices/${estimateId}/`, data),
        confirm: (): Promise<Order> =>
          http.post(`v1/orders/${id}/invoices/${estimateId}/confirm/`),
        revert: (): Promise<Order> =>
          http.post(`v1/orders/${id}/invoices/${estimateId}/revert/`),
        approve: (): Promise<Order> =>
          http
            .post(`v1/orders/${id}/invoices/${estimateId}/approve/`)
            .then((res: any) => {
              useApprovalRequestStore().refresh()
              return res
            }),
        revertApproval: (): Promise<InvoiceWithDetail> =>
          http.post(`v1/orders/${id}/invoices/${estimateId}/revert_approval/`),
      }),
      d2dTasks: (taskId?: string) => ({
        get: (params?: any): Promise<D2DTask> =>
          http.get(`v1/orders/${id}/d2d_tasks/${taskId}/`, { params }),
        list: (params?: any): Promise<Paginated<D2DTask>> =>
          http.get(`v1/orders/${id}/d2d_tasks/`, { params }),
        create: (data: any): Promise<D2DTask> =>
          http.post(`v1/orders/${id}/d2d_tasks/`, data),
        update: (data: any): Promise<D2DTask> =>
          http.put(`v1/orders/${id}/d2d_tasks/${taskId}/`, data),
      }),
      customsTasks: (taskId?: string) => ({
        get: (params?: any): Promise<CustomsTask> =>
          http.get(`v1/orders/${id}/customs_tasks/${taskId}/`, { params }),
        list: (params?: any): Promise<Paginated<CustomsTask>> =>
          http.get(`v1/orders/${id}/customs_tasks/`, { params }),
        create: (data: any): Promise<CustomsTask> =>
          http.post(`v1/orders/${id}/customs_tasks/`, data),
        update: (data: any): Promise<CustomsTask> =>
          http.put(`v1/orders/${id}/customs_tasks/${taskId}/`, data),
      }),

      cargos: (cargoId?: string) => ({
        get: (): Promise<CargoValue> =>
          http.get(`v1/orders/${id}/cargos/${cargoId}/`),
        create: (data: any): Promise<CargoValue> =>
          http.post(`v1/orders/${id}/cargos/`, data),
        update: (data: any): Promise<CargoValue> =>
          http.put(`v1/orders/${id}/cargos/${cargoId}/`, data),
      }),
      commodities: (commodityId?: string) => ({
        get: (): Promise<Commodity> =>
          http.get(`v1/orders/${id}/commodities/${commodityId}/`),
        create: (data: any): Promise<Commodity> =>
          http.post(`v1/orders/${id}/commodities/`, data),
        update: (data: any): Promise<Commodity> =>
          http.put(`v1/orders/${id}/commodities/${commodityId}/`, data),
      }),
      deliveryAddresses: (lid: string) => ({
        create: (data: any): Promise<BaseLocation> =>
          http.post(`v1/orders/${id}/delivery_addresses/`, data), //ない
        update: (data: any): Promise<BaseLocation> =>
          http.put(`v1/orders/${id}/delivery_addresses/${lid}/`, data),
      }),
      counterparties: (tid: string) => ({
        create: (data: any): Promise<TradingPartner> =>
          http.post(`v1/orders/${id}/counterparties/`, data), //ない
        update: (data: any): Promise<TradingPartner> =>
          http.put(`v1/orders/${id}/counterparties/${tid}/`, data),
      }),

      messages: (mid?: number) => ({
        create: (data: any): Promise<Message> =>
          http.post(`v1/orders/${id}/messages/`, data),
        list: (params?: any): Promise<Paginated<Message>> =>
          http.get(`v1/orders/${id}/messages/`, { params }),
      }),

      board: () => ({
        get: (): Promise<Board> => http.get(`v1/orders/${id}/board/`),
      }),

      approximateEstimateCosts: (): Promise<EstimatorResponse> =>
        http.get(`v1/orders/${id}/approximate_estimate_costs/`),

      createEstimateFromApproximation: (
        data: any,
      ): Promise<InvoiceWithDetail> =>
        http.post(`v1/orders/${id}/create_estimate_from_approximation/`, data),
    }),

    d2dTasks: (id?: string) => ({
      get: (params?: any): Promise<D2DTask> =>
        http.get(`v1/d2d_tasks/${id}/`, { params }),
      list: (params?: any): Promise<Paginated<ListD2DTask>> =>
        http.get('v1/d2d_tasks/', { params }),
      create: (data: any): Promise<D2DTask> => http.post('v1/d2d_tasks/', data),
      update: (data: any): Promise<D2DTask> =>
        http.put(`v1/d2d_tasks/${id}/`, data),
      patch: (data: any): Promise<D2DTask> =>
        http.patch(`v1/d2d_tasks/${id}/`, data),

      delivered: (data: any): Promise<D2DTask> =>
        http.post(`v1/d2d_tasks/${id}/delivered/`, data),

      // status: {
      //   update: (data: any): Promise<D2DTask> =>
      //     http.post(`v1/d2d_tasks/${id}/status/`, data),
      // },
      changeStatus: (data: any): Promise<D2DTask> =>
        http.post(`v1/d2d_tasks/${id}/change_status/`, data),
      check: (data: any): Promise<D2DTask> =>
        http.post(`v1/d2d_tasks/${id}/check/`, data),
      register_event: (data: any): Promise<D2DTask> =>
        http.post(`v1/d2d_tasks/${id}/register_event/`, data),
      deliveryArrangement: (
        data: any,
      ): Promise<DeliveryVehicleArrangementTask> =>
        http.post(`v1/d2d_tasks/${id}/delivery_arrangement/`, data),

      warehousingImages: () => ({
        list: (params?: any): Promise<Paginated<WarehousingImage>> =>
          http.get(`v1/d2d_tasks/${id}/warehousing_images/`, {
            params,
          }),
      }),
      recalculate_d2d_costs: (): Promise<EstimatorResponse> =>
        http.post(`v1/d2d_tasks/${id}/recalculate_d2d_costs/`),

      reconfirmBookkeep: (): Promise<D2DTask> =>
        http.post(`v1/d2d_tasks/${id}/reconfirm_bookkeep/`),

      releaseBookkeep: (): Promise<D2DTask> =>
        http.post(`v1/d2d_tasks/${id}/release_bookkeep/`),

      updateInvoiceByPrelaerEtimation: (
        data: Estimation,
      ): Promise<InvoiceWithDetail> =>
        http.post(
          `v1/d2d_tasks/${id}/update_invoice_by_prelaert_etimation/`,
          data,
        ),

      // bookingCompleted: (data: any): Promise<D2DTask> =>
      //   http.post(`v1/d2d_tasks/${id}/booking_completed/`, data),
      // pickupArranged: (data: any): Promise<D2DTask> =>
      //   http.post(`v1/d2d_tasks/${id}/pickup_arranged/`, data),
      // arrivedReceivingWahouse: (data: any): Promise<D2DTask> =>
      //   http.post(`v1/d2d_tasks/${id}/arrived_receiving_warehouse/`, data),
      // arrivedExportBondedWarehouse: (data: any): Promise<D2DTask> =>
      //   http.post(`v1/d2d_tasks/${id}/arrived_export_bonded_warehouse/`, data),
      // exportCustomsCleared: (data: any): Promise<D2DTask> =>
      //   http.post(`v1/d2d_tasks/${id}/export_customs_cleared/`, data),
      // arrivedImportBondedWarehouse: (data: any): Promise<D2DTask> =>
      //   http.post(`v1/d2d_tasks/${id}/arrived_import_bonded_warehouse/`, data),
      // importCustomsCleared: (data: any): Promise<D2DTask> =>
      //   http.post(`v1/d2d_tasks/${id}/import_customs_cleared/`, data),
      // preAlertRegistered: (data: any): Promise<D2DTask> =>
      //   http.post(`v1/d2d_tasks/${id}/pre_alert_registered/`, data),
      // deliveryArranged: (data: any): Promise<D2DTask> =>
      //   http.post(`v1/d2d_tasks/${id}/delivery_arranged/`, data),
    }),

    deliveryVehicleArrangementTasks: (id?: string) => ({
      get: (params?: any): Promise<DeliveryVehicleArrangementTask> =>
        http.get(`v1/delivery_vehicle_arrangement_tasks/${id}/`, { params }),
      update: (data: any): Promise<DeliveryVehicleArrangementTask> =>
        http.put(`v1/delivery_vehicle_arrangement_tasks/${id}/`, data),
      patch: (data: any): Promise<DeliveryVehicleArrangementTask> =>
        http.patch(`v1/delivery_vehicle_arrangement_tasks/${id}/`, data),
      delete: (): Promise<void> =>
        http.delete(`v1/delivery_vehicle_arrangement_tasks/${id}/`),
      sendCarryOutOrder: (): Promise<DeliveryVehicleArrangementTask> =>
        http.post(
          `v1/delivery_vehicle_arrangement_tasks/${id}/send_carry_out_order/`,
        ),
      // 送信前の確認・編集用の下書き(差出人・宛先・CC・既定の件名と本文)
      arrangementRequestDraft: (): Promise<ArrangementRequestDraft> =>
        http.get(
          `v1/delivery_vehicle_arrangement_tasks/${id}/arrangement_request_draft/`,
        ),
      // senderIsCustomer を省略すると顧客の deliverySlipSenderIsCustomer に従う。
      // subject / body が空・省略なら既定の件名・本文で送る。
      // toEmails は下書きの emailCandidates から選ぶ。ccEmails は候補にないアドレスも送れる(共通アドレスは自動で CC に入る)
      sendArrangementRequest: (data: {
        senderIsCustomer?: boolean | null
        toEmails: string[]
        ccEmails?: string[]
        subject?: string
        body?: string
      }): Promise<DeliveryVehicleArrangementTask> =>
        http.post(
          `v1/delivery_vehicle_arrangement_tasks/${id}/send_arrangement_request/`,
          data,
        ),
      // 送信前の確認用。送信されるものと同じPDF
      arrangementRequestPdf: (params: {
        senderIsCustomer: boolean
      }): Promise<AxiosResponse<Blob>> =>
        http.get(
          `v1/delivery_vehicle_arrangement_tasks/${id}/arrangement_request_pdf/`,
          { params, responseType: 'blob' },
        ),
      // 送信履歴に保存された、実際に送ったPDF
      sentArrangementRequestPdf: (
        requestId: string,
      ): Promise<AxiosResponse<Blob>> =>
        http.get(
          `v1/delivery_vehicle_arrangement_tasks/${id}/arrangement_requests/${requestId}/pdf/`,
          { responseType: 'blob' },
        ),
    }),

    customsTasks: (id?: string) => ({
      get: (params?: any): Promise<CustomsTask> =>
        http.get(`v1/customs_tasks/${id}/`, { params }),
      list: (params?: any): Promise<Paginated<ListCustomsTask>> =>
        http.get('v1/customs_tasks/', { params }),
      create: (data: any): Promise<CustomsTask> =>
        http.post('v1/customs_tasks/', data),
      update: (data: any): Promise<CustomsTask> =>
        http.put(`v1/customs_tasks/${id}/`, data),
      changeStatus: (data: any): Promise<CustomsTask> =>
        http.post(`v1/customs_tasks/${id}/change_status/`, data),

      documents: (docId?: string) => ({
        list: (params?: any): Promise<Paginated<CustomsDocument>> =>
          http.get(`v1/customs_tasks/${id}/documents/`, { params }),
        get: (params?: any): Promise<CustomsDocument> =>
          http.get(`v1/customs_tasks/${id}/documents/${docId}/`, { params }),
        create: (data: any): Promise<CustomsDocument> =>
          http.post(`v1/customs_tasks/${id}/documents/`, data),
        update: (data: any): Promise<CustomsDocument> =>
          http.put(`v1/customs_tasks/${id}/documents/${docId}/`, data),
        delete: (): Promise<void> =>
          http.delete(`v1/customs_tasks/${id}/documents/${docId}/`),
      }),
    }),

    overseasBondedWarehouse: (uid?: string) => ({
      list: (params?: any): Promise<Paginated<OverseasBondedWarehouse>> =>
        http.get('v1/overseas_bondedwarehouse/', { params }),
      get: (): Promise<Item> => http.get(`v1/overseas_bondedwarehouse/${uid}/`),
    }),
    bondedWarehouses: (id?: number) => ({
      list: (params?: any): Promise<Paginated<BondedWarehouse>> =>
        http.get('v1/bonded_warehouses/', { params }),
      get: (): Promise<BondedWarehouse> =>
        http.get(`v1/bonded_warehouses/${id}/`),
      create: (data: any): Promise<BondedWarehouse> =>
        http.post('v1/bonded_warehouses/', data),
      update: (data: any): Promise<BondedWarehouse> =>
        http.put(`v1/bonded_warehouses/${id}/`, data),
    }),

    ports: (id?: number) => ({
      list: (params?: any): Promise<Paginated<Port>> =>
        http.get('v1/ports/', { params }),
      get: (): Promise<Port> => http.get(`v1/ports/${id}/`),
    }),

    seaSchedules: (id?: number) => ({
      list: (params?: any): Promise<Paginated<SeaSchedule>> =>
        http.get('v1/sea_schedules/', { params }),
      get: (): Promise<SeaSchedule> => http.get(`v1/sea_schedules/${id}/`),
      create: (data: any): Promise<SeaSchedule> =>
        http.post('v1/sea_schedules/', data),
      update: (data: any): Promise<SeaSchedule> =>
        http.put(`v1/sea_schedules/${id}/`, data),
      registerDelay: (data: any): Promise<SeaSchedule> =>
        http.post(`v1/sea_schedules/${id}/register_delay/`, data),
    }),
    vessels: (id?: number) => ({
      list: (params?: any): Promise<Paginated<Vessel>> =>
        http.get('v1/vessels/', { params }),
      get: (): Promise<Vessel> => http.get(`v1/vessels/${id}/`),
      create: (data: any): Promise<Vessel> => http.post('v1/vessels/', data),
      update: (data: any): Promise<Vessel> =>
        http.put(`v1/vessels/${id}/`, data),
      delete: (): Promise<void> => http.delete(`v1/vessels/${id}/`),
    }),

    nominal: (id?: number) => ({
      list: (params?: any): Promise<Paginated<NominalInfo>> =>
        http.get('v1/nominals/', { params }),
      get: (): Promise<NominalInfo> => http.get(`v1/nominals/${id}/`),
    }),

    // 支払請求書PDF 取り込み(モデルレス。非同期抽出 + PDF単位登録)
    procurementInvoiceImports: () => ({
      // {imports:[{file(base64), filename}], dateFrom?, dateTo?} -> [{taskId, filename}]
      // dateFrom/dateTo は突合候補(納品予定日レンジ)の絞り込み(未指定なら60日前〜今日)
      parse: (data: {
        imports: { file: string; filename: string }[]
        dateFrom?: string
        dateTo?: string
        extraPrompt?: string
      }): Promise<ProcurementInvoiceParseTask[]> =>
        http.post('v1/procurement_invoice_imports/parse/', data),
      // taskId の状態/結果を取得(ポーリング)
      result: (taskId: string): Promise<ProcurementInvoiceParseResult> =>
        http.get('v1/procurement_invoice_imports/result/', {
          params: { taskId },
        }),
      // {taskId, rows, (記録用) filename/payee/invoiceNo/invoiceDate/dateFrom/dateTo/validation} を本登録
      register: (data: {
        taskId: string
        rows: ProcurementInvoiceRow[]
        filename?: string
        payee?: string | null
        invoiceNo?: string | null
        invoiceDate?: string | null
        dateFrom?: string
        dateTo?: string
        extraPrompt?: string
        validation?: ProcurementInvoiceValidation
      }): Promise<ProcurementInvoiceRegisterResult> =>
        http.post('v1/procurement_invoice_imports/register/', data),
      // 登録記録の一覧(ページネーション)。reverted=true/false で取り消し済み/未取り消しを絞り込める
      registrations: (
        params?: any,
      ): Promise<Paginated<ProcurementInvoiceRegistration>> =>
        http.get('v1/procurement_invoice_imports/registrations/', { params }),
      // 取り込み単位の取り消し。紐づく明細を削除し、記録には取り消し済みを立てる。
      // 締め済み受注の明細を含む場合は 403、取り消し済み/対象なしは 400(detail に理由)
      revertRegistration: (
        id: number,
      ): Promise<ProcurementInvoiceRevertResult> =>
        http.post(`v1/procurement_invoice_imports/registrations/${id}/revert/`),
    }),

    approvalRequests: (id?: string) => ({
      list: (params?: any): Promise<Paginated<ApprovalRequest>> =>
        http.get('v1/approval_requests/', { params }),
      get: (): Promise<ApprovalRequest> =>
        http.get(`v1/approval_requests/${id}/`),
      count: (): Promise<{ count: number }> =>
        http.get('v1/approval_requests/count/'),
    }),

    boards: (id?: string) => ({
      list: (params?: any): Promise<Paginated<Board>> =>
        http.get('v1/boards/', { params }),
      get: (): Promise<Board> => http.get(`v1/boards/${id}/`),
      bookmark: (data: any): Promise<void> =>
        http.post(`v1/boards/${id}/bookmark/`, data),
      subscribe: (data?: any): Promise<void> =>
        http.post(`v1/boards/${id}/subscribe/`, data),
      mute: (data?: any): Promise<void> =>
        http.post(`v1/boards/${id}/mute/`, data),
      unmute: (data?: any): Promise<void> =>
        http.post(`v1/boards/${id}/unmute/`, data),
      invite: (data: InviteBoard): Promise<void> =>
        http.post(`v1/boards/${id}/invite/`, data),
      messages: (mid?: number) => ({
        create: (data: any): Promise<Message> =>
          http.post(`v1/boards/${id}/messages/`, data),
        list: (params?: any): Promise<Paginated<Message>> =>
          http.get(`v1/boards/${id}/messages/`, { params }),
      }),
    }),
    subscribedBoards: (id?: string) => ({
      list: (params?: any): Promise<Paginated<Board>> =>
        http.get('v1/subscribed_boards//', { params }),
      get: (): Promise<Board> => http.get(`v1/subscribed_boards/${id}/`),
    }),

    ordersBillingAndPayments: () => ({
      list: (params?: any): Promise<Paginated<OrderBillingAndPayment>> =>
        http.get('v1/orders/billing-and-payments/', { params }),
    }),

    issuedInvoices: (id?: string) => ({
      list: (params?: any): Promise<Paginated<InvoiceWithDetail>> =>
        http.get('v1/issued_invoices/', { params }),
      get: (): Promise<InvoiceWithDetail> =>
        http.get(`v1/issued_invoices/${id}/`),
      // create: (data: any): Promise<InvoiceWithDetail> =>
      //   http.post('v1/invoices/', data),
      update: (data: any): Promise<InvoiceWithDetail> =>
        http.put(`v1/issued_invoices/${id}/`, data),
      patch: (data: any): Promise<InvoiceWithDetail> =>
        http.patch(`v1/issued_invoices/${id}/`, data),
      payByStripe: (data: any): Promise<InvoiceWithDetail> =>
        http.post(`v1/issued_invoices/${id}/pay_by_stripe/`, data),
      toClearedStatus: (): Promise<InvoiceWithDetail> =>
        http.post(`v1/issued_invoices/${id}/to_cleared_status/`),
      exportCsv: (params?: any): Promise<AxiosResponse<Blob>> =>
        http.get(`v1/issued_invoices/export_csv/`, {
          params,
          responseType: 'blob',
        }),
      exportSalesSummaryCsv: (params?: {
        dateFrom?: string
        dateTo?: string
      }): Promise<AxiosResponse<Blob>> =>
        http.get(`v1/issued_invoices/export_sales_summary_csv/`, {
          params,
          responseType: 'blob',
        }),
    }),
    stripePaymentIntents: (id?: number) => ({
      get: (): Promise<StripePaymentIntent> =>
        http.get(`v1/stripe_payment_intents/${id}/`),
      list: (params?: any): Promise<Paginated<StripePaymentIntent>> =>
        http.get('v1/stripe_payment_intents/', {
          params,
        }),
    }),
    exchangeRates: () => ({
      list: (
        params?: any,
      ): Promise<{ results: { [key: string]: ExchangeRateData } }> =>
        http.get('v1/exchange_rates/', { params }),
    }),

    estimator: () => ({
      quick: (data: any): Promise<QuickEstimatorResponse> =>
        http.post('v2/estimator/quick/', data),
      createToken: (): Promise<{ token: string }> =>
        http.post('v1/estimator/token/'),
    }),
  }
}

let api = null as null | ReturnType<typeof createAPI>

export const useFetchAll = <T>(fn: () => Promise<Paginated<T>>) => {
  const api = useAPI()
  const items: Ref<T[]> = ref([])
  const error: Ref<null | Error> = ref(null)
  const abort = ref(false)
  const fetchAll = async () => {
    error.value = null
    abort.value = false
    items.value = []
    let { results, next } = await fn()
    items.value = results
    while (!abort.value && next) {
      try {
        const resp: Paginated<T> = await api.http.get(next)
        next = resp.next
        items.value = items.value.concat(resp.results)
      } catch (err) {
        console.error(err)
        error.value = err as Error
      }
    }
  }
  return {
    items,
    fetchAll,
    abort,
    error,
  }
}

export const fetchDetailOr404 = async <T>(detDetail: () => Promise<T>) => {
  try {
    return await detDetail()
  } catch (err: any) {
    if (err.status == 404) {
      showError({ statusCode: 404, message: 'NotFound' })
    } else {
      throw err
    }
  }
}

export const useAPI = () => {
  if (!api) api = createAPI()
  return api
}

export type Paginated<T> = {
  results: T[]
  next: null | string
  previous: null | string
}

export const setValidationError = (
  setErrors: Function,
  error: unknown | Error,
  errDict?: Ref<Record<string, any>>,
) => {
  if (error instanceof AxiosError) {
    const resp = error.response
    if (
      typeof resp?.headers.get === 'function' &&
      resp.headers.get('Content-Type')?.toString().includes('application/json')
    ) {
      const errors = camelProps(resp?.data)
      if (error.status === 400) {
        setErrors(errors)
        if (errDict) errDict.value = errors
      } else {
        const e = { nonFieldErrors: [errors.detail] }

        setErrors(e)
        if (errDict) errDict.value = e
      }
    }
  }
}

export const getValidationError = (
  error: unknown | Error,
): Record<string, any> => {
  if (error instanceof AxiosError) {
    const resp = error.response
    if (
      typeof resp?.headers.get === 'function' &&
      resp.headers.get('Content-Type')?.toString().includes('application/json')
    ) {
      const errors = camelProps(resp?.data)
      if (error.status === 400) {
        return errors
      } else {
        const e = { nonFieldErrors: [errors.detail] }
        return e
      }
    }
  }
  return {}
}
export type User = {
  id: string
  email: string
  fullName: string
  familyName: string
  givenName: string
  dateJoined: string
  isActive: boolean
  isStaff: boolean

  organization: string
  organizationInfo?: {
    id: string
    name: string
    nameJa: string
  }
}

/**
 * スタッフの自アカウント用 (`v1/my/` の返り値)。
 * `User` に full_permissions などの拡張フィールドを加えたもの。
 */
export type MyUser = User & {
  allPermissions: string[]
}

export type SimpleUser = {
  id: string
  email: string
  fullName: string
  organization: string
}

export type BaseAddressContact = {
  // countryを入れるべきかが悩ましい
  id: string
  name: string
  postalCode: string
  address: string
  addressJa: string
  contactPersonnel: string
  contactEmail: string
  contactPhonenumber: string
}

export type Organization = BaseAddressContact & {
  nameJa: string
  corporateNumber: string
  invoiceNumber: string
  importerExporterCode: string
  isClient: boolean
  isPartner: boolean
  isAgency: boolean
  isAffiliatedCompany: boolean
  isTransportCompany: boolean
  hasDirectDebitAccount: boolean
  requiresAdvancePayment: boolean
  // 送り状の扱い先を顧客名義にする
  deliverySlipSenderIsCustomer: boolean
  // スタッフが送る依頼メール(配車依頼など)の送付先。スタッフ向けの組織編集API でだけ返る
  emailRecipients?: OrganizationEmailRecipient[]

  fcCustomerId: string

  salesClose: number | null
  salesSite: number | null
  salesPayday: number | null
  salesLimit: string | null
  advanceClose: number | null
  advanceSite: number | null
  advancePayday: number | null
  advanceLimit: string | null
  costsClose: number | null
  costsSite: number | null
  costsPayday: number | null
  costsLimit: string | null

  deleted?: boolean

  readonly created?: string
  readonly updated?: string
  readonly stripeCustomerId?: string
}

// 組織のメール宛先。isPrimary は画面上の「既定の宛先」で1社1件、送信時の To の初期値になる
export type OrganizationEmailRecipient = {
  email: string
  label: string
  isPrimary: boolean
}

export type TradingPartner = BaseAddressContact & {
  country: number
  addresseeName: string
  countryInfo?: Country
  wechatId: string
  lineId: string
}

export type BaseLocation = BaseAddressContact & {
  country: number
  countryInfo?: Country
  addresseeName: string
  addresseeNameJa: string
  lineId: string
  wechatId: string
  domestic: boolean
}

export type ItemImage = {
  readonly id?: string
  item: string
  file: string
  filename: string
  thumbnail?: string
  index: number
  created: string
  updated: string
  deleted: boolean
}

export type CreateImage = {
  file: string
  filename: string
}

export type Item = {
  id: string
  name: string
  manufacturer: string
  code: string
  hsCode: string
  note: string
  unitPrice: string
  currency: number
  images: ItemImage[]
  created: string
  updated: string
  deleted: boolean
}

export type Country = {
  id: number
  name: string
  nameja: string
}

export type Port = {
  id: number
  name: string
  nameJa: string
  code: string
  portType: Number
  country: Country

  getName(): () => string
}

export type OverseasBondedWarehouse = {
  id: number
  name: string
  nameJa: string
  port: Port
  note: string
}

export type CustomsPoa = {
  id: string
  file: string
  filename: string
  effectiveDate: string
  expirationDate: string | null
  autoRenewal: boolean
  created: string
  updated: string
  deleted: boolean
  disabled: boolean
  locked: boolean
}
export type Vessel = {
  readonly id: number
  name: string
  carrier: string
}
export type SeaSchedule = {
  readonly id: number
  vessel: number
  vesselInfo: Vessel
  pol: number
  polInfo: Port
  pod: number
  podInfo: Port
  voy: string
  etd: string
  eta: string
  initialEtd: string
  initialEta: string
  delayed: boolean
  skipped: boolean
  hdsCanceled: boolean
}

export type D2DTaskEvent = {
  eventType: number
  comment: string
  created: string
}

export type WarehousingImage = {
  readonly id: string
  imageType: number
  file: string
  filename: string
  thumbnail: string
  broken: boolean
  created: string
  updated: string
}

export type D2DTask = {
  readonly id: string
  status: number
  readonly order: OrderInfoWithDetail
  pol: null | number
  polInfo?: Port
  pod: null | number
  podInfo?: Port
  schedule: null | number
  readonly scheduleInfo: SeaSchedule
  pic: null | string
  readonly picInfo: SimpleUser
  agencyPic: null | string
  readonly agencyPicInfo: SimpleUser

  appCode: string

  // pre alert
  blNumber: string
  package: number
  packageStr: string
  quantity: number
  grossWeight: string
  measurement: string
  shippingMark: string
  commodity: string

  events: D2DTaskEvent[]
  cargos: CargoValue[]
  commodities: CommodityValue[]
  deliveryVehicleArrangementTasks: SimpleDeliveryVehicleArrangementTask[]
  readonly customsTasks: CustomsTaskInfo[]
  //warehousingImages: WarehoungingImage[]

  skdPickupDate: string
  skdReceivedDate: string
  skdDeliveryDate: string

  // 作業チェックリスト
  bookingCompleted: boolean
  exportCfsRequested: boolean
  pickupArranged: boolean
  exportCustomsRequested: boolean
  importCustomsRequested: boolean
  deliveryArranged: boolean
  importCfsRequested: boolean

  // イベント関連状態フラグ
  arrivedAtExportWarehouse: boolean
  exportCustomsCleared: boolean
  arrivedAtImportWarehouse: boolean
  importCustomsCleared: boolean
  shippedFromImportWarehouse: boolean
  delivered: boolean

  // システム関連状態フラグ
  preAlertRegistered: boolean
  arrivalNoticeSettled: boolean

  dolessNumber: string
  carryOutNumber: string

  seaJobNo: string
  seaSubNo: number
  seaJobid: string
  seaJobExpenses: Record<string, any>
  ctmJobcode: string
  ctmJobExpenses: Record<string, any>
}

export type CreateD2DTask = {
  pic: string
  agencyPic: string
}

export type D2DTaskInfo = {
  readonly id: string
  readonly appCode: string

  readonly polInfo: Port
  readonly podInfo: Port
  readonly scheduleInfo: SeaSchedule

  readonly skdPickupDate: string
  readonly skdReceivedDate: string
  readonly skdDeliveryDate: string

  // 作業チェックリスト
  readonly bookingCompleted: boolean
  readonly exportCfsRequested: boolean
  readonly pickupArranged: boolean
  readonly exportCustomsRequested: boolean
  readonly importCustomsRequested: boolean
  readonly deliveryArranged: boolean
  readonly importCfsRequested: boolean

  // イベント関連状態フラグ
  readonly arrivedAtExportWarehouse: boolean
  readonly exportCustomsCleared: boolean
  readonly arrivedAtImportWarehouse: boolean
  readonly importCustomsCleared: boolean
  readonly shippedFromImportWarehouse: boolean
  readonly delivered: boolean

  // システム関連状態フラグ
  readonly preAlertRegistered: boolean
  readonly arrivalNoticeSettled: boolean

  //prealert
  readonly blNumber: string
  readonly package: number
  readonly packageStr: string
  readonly quantity: number
  readonly grossWeight: string
  readonly measurement: string
  readonly shippingMark: string
  commodity: string

  // delivery
  readonly dolessNumber: string
  readonly carryOutNumber: string

  //schedule info
  readonly callSign: string
  readonly vesselName: string
  readonly voy: string
  readonly etd: string
  readonly eta: string
}

export type ListD2DTask = {
  readonly id: string
  readonly appCode: string

  readonly polInfo: Port
  readonly podInfo: Port
  readonly scheduleInfo: SeaSchedule

  readonly skdPickupDate: string
  readonly skdReceivedDate: string
  readonly skdDeliveryDate: string

  readonly order: OrderInfoWithDetail
}

export type ListD2DTaskInfo = {
  readonly id: string
  readonly appCode: string

  readonly vesselName: string
  readonly voy: string
  readonly etd: string
  readonly eta: string
  readonly callSign: string

  readonly skdPickupDate: string
  readonly skdReceivedDate: string
  readonly skdDeliveryDate: string

  readonly dolessNumber: string
  readonly carryOutNumber: string
}

export type CustomsTask = {
  readonly id: string
  readonly status: number
  readonly order: OrderInfoWithDetail
  readonly appTask: D2DTaskInfo
  cargos: CargoValue[]
  commodities: CommodityValue[]
  documents: CustomsDocument[]
}

export type ListCustomsTask = {
  readonly id: string
  readonly status: number
  readonly order: OrderInfoWithDetail
  readonly appTask: ListD2DTaskInfo
}

export type CustomsTaskInfo = {
  readonly id: string
  readonly documents: CustomsDocument[]
}

export type CustomsDocument = {
  id: string
  type: number
  file: string
  filename: string
  task: string
  created?: string
}

export type Document = {
  readonly id: number
  file: string
  filename: string
  created: string
}

export type NominalInfo = {
  readonly id: number
  name: string
  nameEn: string
  dealType: string
  taxRate: number
}

export type ExchangeRateData = {
  rate: string
  date: string
  name: string
}

export type ExchangeRateInput = {
  [currencyId: string]: {
    rate: string
    date: string
    name: string
  }
}

export type DetailedStatement = {
  readonly id?: number
  position: number
  quantity: string
  unitPrice: string // 設定したcurrencyでの単価
  taxRate: number | null
  nominal: number | null
  readonly nominalInfo?: NominalInfo
  currency: number // Currency ID (1=JPY, 2=USD, 3=RMB)
  exchangeRate?: string // Exchange rate (set automatically)
  localSubtotal?: string // 設定されたCurrencyでの小計
  unit: number | null // Keep for backwards compatibility
  subtotal?: string // 日本円での小計
  note: string
}

export type ReimbursementStatement = {
  readonly id?: number
  payee: string | null
  readonly payeeInfo?: Organization
  position: number
  nominal: string
  total: string
  taxRate: number | null
  salesTax: string
  accrualDate: string
}

export type Invoice = {
  readonly id?: string
  readonly order?: string
  readonly orderInfo?: OrderInfo
  readonly confirmed?: string
  readonly approved?: string
  readonly cleared?: string

  readonly subtotal?: string
  readonly salesTax?: string
  readonly total?: string
  readonly reimbursementTotal?: string
  readonly status?: number
  readonly deleted?: boolean
  readonly updated?: string
  readonly created?: string
  readonly anReflected?: boolean
  /** @deprecated use anReflected */
  readonly prealertReflected?: boolean
  note: string
  accrualDate: string
  dueDate: string
  exchangeRateDate?: string | null // Exchange rate application date
  exchangeRates?: any // Exchange rates used for calculation
}

export type InvoiceWithDetail = Invoice & {
  detailedStatements: DetailedStatement[]
  reimbursementStatements: ReimbursementStatement[]
  documents: Document[]
  readonly downloadUrl?: string
  readonly filename?: string

  readonly paymentHistories?: PaymentHistory[]
}

// 請求・費用サマリ一覧(v1/orders/billing-and-payments/)用の型
export type OverviewIssuedInvoice = {
  readonly id: string
  readonly status: number
  readonly total: string
  readonly accrualDate: string
  readonly hasInsurance: boolean
}

export type OverviewProcurementExpense = {
  readonly id: number
  readonly hasTruckCost: boolean
  readonly hasInsurance: boolean
  readonly confirmed: boolean
  readonly confirmedAt: string | null
  readonly subtotal?: string
  readonly salesTax?: string
  readonly total?: string
  readonly truckCostRegistered?: boolean
}

export type BillingOrderInfo = {
  readonly id: string
  readonly orderCode: string
  readonly status: number
  readonly paymentMethod: string
  readonly organization: string
  readonly organizationInfo: {
    readonly id: string
    readonly name: string
    readonly nameJa: string
  }
  readonly counterpartyName: string
  readonly counterpartyAddress: string
  readonly note: string
  readonly internalNote: string
}

export type OrderBillingAndPayment = {
  readonly order: BillingOrderInfo
  readonly d2dTasks: SimpleD2DTask[]
  readonly deliveryVehicleArrangementTasks: MinimalDeliveryVehicleArrangementTask[]
  readonly issuedInvoices: OverviewIssuedInvoice[]
  readonly procurementExpense: OverviewProcurementExpense | null
}

// 調達費用(ヘッダー+明細)。オーダーと1:1のシングルトン。
// v1/orders/{order}/procurement_expense/ で取得・更新する。今後リッチ化予定。
export type ProcurementExpenseDetail = {
  id?: number
  order?: string
  orderInfo?: any
  confirmed?: boolean
  task?: string | null
  nominal: number | null
  nominalInfo?: NominalInfo | null
  partner?: string | null
  partnerInfo?: Organization | null
  position?: number
  currency: number | null
  unitPrice: string
  quantity: string
  unit: number | null
  subtotal?: string
  exchangeRate?: string | null
  subtotalJa?: string
  taxRate: number | null
  accrualDate?: string | null
  note?: string
  // 表示用(フロント計算のプレビュー)
  localSubtotal?: string
  updated?: string
  created?: string
}

// --- 支払請求書PDF 取り込み(モデルレス)。以下はレスポンス型のみ ---
export type ProcurementInvoiceOrderInfo = {
  appCode?: string | null
  orderUid?: string | null
  orderId?: number | null
  vessel?: string | null
  customer?: string | null
  skdDeliveryDate?: string | null
  ctmJobcode?: string | null
}

// 抽出された明細行(画面で手修正する対象)。
// v-model にバインドする項目(partner/accrualDate 等)は defineModel の型に合わせ
// undefined を含めない(string | null)。normalizeRow で欠損を補完する。
export type ProcurementInvoiceRow = {
  costItem: string
  nominal: number | null
  nominalName?: string | null
  taxRate: number | null
  appCode: string | null
  orderInfo?: ProcurementInvoiceOrderInfo | null
  partner: string | null
  partnerName?: string | null
  amount: string
  accrualDate: string | null
  note: string
  matchStatus?: 'matched' | 'unmatched'
  unmatchedReason?: string | null
}

export type ProcurementInvoiceValidation = {
  payee?: string
  invoiceTotals?: { total?: number | string; tax?: number | string }
  computedTotals?: {
    taxable?: number | string
    nonTaxable?: number | string
    tax?: number | string
    total?: number | string
  }
  // 差異はエージェント出力に依存(数値 / 文字列 / {total, tax} 等のオブジェクト)。
  // 表示・判定は invoiceTotals vs computedTotals から自前計算する(totalDiff/taxDiff)。
  diff?: number | string | Record<string, number | string> | null
  excludedItems?: { item?: string; amount?: number | string; reason?: string }[]
  // 差異の判定(LLMが設定): 'none' | 'explained' | 'unexplained'
  diffStatus?: 'none' | 'explained' | 'unexplained' | string | null
  notes?: string
}

// parse エンドポイントの返り(投入したジョブ)
export type ProcurementInvoiceParseTask = {
  taskId?: string
  filename?: string
  error?: string
}

// result エンドポイントの返り(ポーリング結果)
export type ProcurementInvoiceParseResult = {
  taskId: string
  status: 'pending' | 'succeeded' | 'failed'
  filename?: string | null
  rows?: ProcurementInvoiceRow[]
  validation?: ProcurementInvoiceValidation
  payee?: string | null
  invoiceNo?: string | null
  invoiceDate?: string | null
  error?: string
}

// 登録記録の明細スナップショット
export type ProcurementInvoiceRegistrationDetail = {
  appCode?: string | null
  orderUid?: string | null
  nominal?: number | null
  nominalName?: string | null
  partner?: string | null
  partnerName?: string | null
  taxRate?: number | null
  amount?: string
  accrualDate?: string | null
  note?: string | null
}

// 登録記録(import画面の履歴リスト)
export type ProcurementInvoiceRegistration = {
  id: number
  taskId?: string
  filename?: string
  payee?: string
  invoiceNo?: string
  invoiceDate?: string | null
  dateFrom?: string | null
  dateTo?: string | null
  extraPrompt?: string | null
  createdCount: number
  orderCount: number
  skippedCount: number
  totalAmount: string
  details?: ProcurementInvoiceRegistrationDetail[]
  skipped?: { index: number; reason: string }[]
  validation?: ProcurementInvoiceValidation
  registeredBy?: number | null
  registeredByInfo?: SimpleUser | null
  created: string
  // 取り消し(revert)の記録。取り消し済みでも記録自体は履歴に残る
  reverted: boolean
  revertedAt?: string | null
  revertedBy?: number | null
  revertedByInfo?: SimpleUser | null
  revertedCount?: number
  // この取り込みに紐づいて現存する明細数
  detailCount: number
  // 紐づく明細のうち締め済み受注のもの(1件でもあると取り消しできない)
  confirmedDetailCount: number
}

export type ProcurementInvoiceRegisterResult = {
  created: number
  orders: number
  skipped: { index: number; reason: string }[]
  registration?: ProcurementInvoiceRegistration | null
}

// revert(取り込みの取り消し)の結果
export type ProcurementInvoiceRevertResult = {
  // 削除した明細数
  deleted: number
  // 集計を再計算した受注数
  orders: number
  // 取り消し後の登録記録
  registration: ProcurementInvoiceRegistration
}

// 調達費用に添える D2DTask の最小情報(B/L番号 + FCDB Expense スナップショット)。read-only。
export type ProcurementD2DTask = {
  id: string
  appCode: string
  seaJobNo: string
  seaSubNo: number
  seaJobid: string
  ctmJobcode: string

  // { items: [{ costname, total, type }, ...] } 形式のスナップショット
  seaJobExpenses?: Record<string, any> | null
  ctmJobExpenses?: Record<string, any> | null
}

// 調達費用に添える配車タスク(DeliveryVehicleArrangementTask)の基本情報。read-only。
export type ProcurementDeliveryVehicleArrangementTask = {
  id: string
  code: string
  appCode: string
  truckOption?: number
  pickupDate?: string | null
  deliveryDate?: string | null
}

export type ProcurementExpense = {
  id?: number
  order: string
  orderInfo?: BillingOrderInfo
  confirmed?: boolean
  confirmedAt?: string | null
  confirmedBy?: number | null
  subtotal?: string
  salesTax?: string
  total?: string
  truckCostRegistered: boolean
  insuranceCostRegistered: boolean
  note: string
  details: ProcurementExpenseDetail[]
  d2dTasks?: ProcurementD2DTask[]
  deliveryVehicleArrangementTasks?: ProcurementDeliveryVehicleArrangementTask[]
  issuedInvoices?: InvoiceWithDetail[]
  updated?: string
  created?: string
}

export type SourceInfo = {
  readonly id: string
  readonly type: string
  readonly status: number | string
  readonly statusText: string
  //他オプショナルなフィールドがあり
  readonly partner?: Organization
}

export type ApprovalRequest = {
  readonly id: string
  readonly order?: Order
  readonly source?: SourceInfo
  readonly permissionCode: string
  readonly approved: boolean
  readonly created: string
  readonly updated: string
  readonly deleted: boolean
}
export type StripePaymentIntent = {
  readonly stripeId: string
}

export type BondedWarehouse = {
  id: number
  name: string
  naccsCode: string
  warehouseCode: string
  tel: string
  fax: string
  pic: string
  port: number
  portInfo: Port
  address: string
  isActive: boolean
}

export type PaymentHistory = {
  id: number
  status: string
  amount: number
}

export function warehousePortName(wh?: OverseasBondedWarehouse): string {
  if (wh?.port?.nameJa) {
    return wh.port.nameJa
  } else if (wh?.port.name) {
    return wh.port.name
  }
  return ''
}

export const fetchPortList = (params?: any) => {
  const api = useAPI()
  return api.ports().list({ ...params, use_app: true })
}

export const fetchCountries = async (params?: any) => {
  const api = useAPI()
  return api.countries().list({ ...params, useApp: true })
}

export const fetchVesselList = (params?: any) => {
  const api = useAPI()
  return api.vessels().list({ ...params })
}

export const fetchMyLocations = async (organization: string, params?: any) => {
  const api = useAPI()
  params['domestic'] = true
  return api
    .organizations(organization)
    .locations()
    .list({ ...params })
}

export const fetchOtherLocations = async (
  organization: string,
  params?: any,
) => {
  const api = useAPI()
  params['domestic'] = false
  return api
    .organizations(organization)
    .locations()
    .list({ ...params })
}
